import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth, SignedIn, SignedOut, RedirectToSignIn } from "@clerk/clerk-react";
import type { BreakdownResponse, VendorCost, CategoryCost } from "@bridepath/shared";
import { apiGet } from "../lib/api.js";
import { AuthedImage } from "../lib/AuthedImage.js";

interface MomentOut {
  id: string; label: string; ordinal: number;
  qa_score: number | null; current_version: number; status: string;
  image_url: string | null;
}
interface PlannedLabel { label: string; ordinal: number }
interface JobResponse {
  job: {
    id: string; package_id: string | null;
    status: "queued" | "running" | "done" | "failed" | "partial";
    error: string | null;
    // Full ordered scene plan (0032) — checklist shows ALL of these from the
    // first poll so it never grows 6→10. Empty on legacy jobs (PROVISIONAL).
    planned_labels?: PlannedLabel[];
  };
  moments: MomentOut[];
}

const API_BASE = (): string =>
  (window as unknown as { __BRIDEPATH_API__?: string }).__BRIDEPATH_API__ ?? "";

const dollars = (cents: number): string => `$${Math.round(cents / 100).toLocaleString()}`;
const range = (lo: number, hi: number): string => (lo === hi ? dollars(lo) : `${dollars(lo)}–${dollars(hi)}`);

// The provisional scene list shown on the generating screen before the job has
// reported its real moments.
const PROVISIONAL = ["Venue", "Florals", "Tablescape", "Ceremony", "Reception", "First Dance", "Cake"];

export function Vision() {
  return (
    <>
      <SignedOut><RedirectToSignIn /></SignedOut>
      <SignedIn><VisionInner /></SignedIn>
    </>
  );
}

function VisionInner() {
  const { jobId } = useParams<{ jobId: string }>();
  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const stableGetToken = useCallback(() => getTokenRef.current(), []);

  const [data, setData] = useState<JobResponse | null>(null);
  const [breakdown, setBreakdown] = useState<BreakdownResponse | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownErr, setBreakdownErr] = useState<string | null>(null);
  // Frontend hang backstop: if the job never reaches a terminal status (the
  // consumer was killed mid-render before its terminal write), the backend
  // reaper finalizes it within minutes — but until then this screen would poll
  // "Bringing your day to life…" forever. Give up after a stall (no new scene
  // for STALL_MS) or an absolute ceiling, mirroring Plan.tsx's ComposingOverlay,
  // so the user lands on a graceful "didn't finish" screen instead of a spinner.
  const [gaveUp, setGaveUp] = useState(false);

  // Poll the job until it's terminal; then stop (the preview is now static).
  useEffect(() => {
    if (!jobId) return;
    const STALL_MS = 300_000;     // no new scene composed for 5 min ⇒ assume stuck
    const ABS_MS = 1_200_000;     // 20 min absolute ceiling — never strand the user
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const startMs = Date.now();
    let lastProgressMs = Date.now();
    let prevDone = 0;
    const poll = async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const res = await apiGet<JobResponse>(`/jobs/${jobId}`, token);
      if (cancelled) return;
      if (res.ok) {
        setData(res.value);
        const s = res.value.job.status;
        if (s === "queued" || s === "running") {
          const done = res.value.moments.filter((mm) => mm.image_url).length;
          if (done > prevDone) { prevDone = done; lastProgressMs = Date.now(); }
          const now = Date.now();
          if (now - lastProgressMs > STALL_MS || now - startMs > ABS_MS) { setGaveUp(true); return; }
          timer = setTimeout(poll, 3_000);
        }
      } else {
        if (Date.now() - startMs > ABS_MS) { setGaveUp(true); return; }
        timer = setTimeout(poll, 5_000);
      }
    };
    void poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [jobId, getToken]);

  const packageId = data?.job.package_id ?? null;
  const status = data?.job.status;
  const terminal = status === "done" || status === "partial" || status === "failed";

  // Fetch the cost breakdown once the job is terminal AND the package is known —
  // no point pricing vendors while images are still rendering. Refetchable.
  // Fetch the cost breakdown once the job is terminal. Retries with capped
  // backoff and NEVER gives up, so the readiness gate can insist on real cost
  // data without ever trapping the user on the generating screen. `breakdownErr`
  // is advisory ("still pricing…"), NOT a reveal trigger — we never reveal a
  // cost-less preview.
  const loadBreakdown = useCallback(async () => {
    if (!packageId) return;
    setBreakdownLoading(true);
    setBreakdownErr(null);
    for (let attempt = 0; ; attempt++) {
      if (attempt) await new Promise((r) => setTimeout(r, Math.min(15_000, 1_000 * 2 ** attempt)));
      const token = await getToken();
      if (!token) continue;
      const res = await apiGet<BreakdownResponse>(`/packages/${packageId}/breakdown`, token);
      if (res.ok) { setBreakdown(res.value); setBreakdownErr(null); setBreakdownLoading(false); return; }
      if (attempt >= 2) setBreakdownErr("Still pricing your vendors…");
    }
  }, [packageId, getToken]);

  useEffect(() => {
    if (terminal && packageId && !breakdown && !breakdownLoading) void loadBreakdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terminal, packageId]);

  // ── Readiness gate ─────────────────────────────────────────────────────────
  // The finished preview is shown ONLY when EVERYTHING is ready: every planned
  // scene rendered (allImagesReady) AND the wedding fully costed out (breakdown
  // DATA present — an error is NOT "resolved"; we keep retrying). This holds
  // uniformly, so a transient pricing failure can never reveal a cost-less page.
  // partial/failed-with-images is handled explicitly below (a truncated wedding
  // shouldn't masquerade as the finished day); a total failure shows an error.
  const sorted = data ? [...data.moments].sort((a, b) => a.ordinal - b.ordinal) : [];
  const allImagesReady = sorted.length > 0 && sorted.every((m) => Boolean(m.image_url));
  const noImages = sorted.every((m) => !m.image_url);
  const hardFail = Boolean(terminal) && noImages;
  const costResolved = breakdown != null;
  const previewReady = !hardFail && costResolved && allImagesReady;

  if (!data) {
    return <GeneratingScreen moments={[]} planned={[]} terminal={false} costing={false} />;
  }
  if (hardFail) {
    return (
      <section className="max-w-lg mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl">We couldn't compose your wedding</h1>
        <p className="mt-3 text-stone-600">{data.job.error ?? "Something went wrong while generating. Please try again."}</p>
        <Link to="/plan" className="inline-block mt-6 px-6 py-3 rounded-full bg-[var(--bp-accent)] text-white text-sm font-medium hover:opacity-90">Back to the builder</Link>
      </section>
    );
  }
  // A partial/failed run that produced SOME (but not all) images can't show the
  // finished preview — it would be a truncated wedding with scenes silently
  // missing. Tell the couple plainly and let them regenerate.
  if ((status === "partial" || status === "failed") && !noImages && !allImagesReady) {
    return (
      <section className="max-w-lg mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl">A few scenes didn't finish</h1>
        <p className="mt-3 text-stone-600">
          We composed {sorted.filter((m) => m.image_url).length} of your scenes, but a couple couldn't be rendered this time. Regenerate to compose your whole day.
        </p>
        <Link to="/plan" className="inline-block mt-6 px-6 py-3 rounded-full bg-[var(--bp-accent)] text-white text-sm font-medium hover:opacity-90">Back to the builder</Link>
      </section>
    );
  }
  // Backstop: the job stalled (or the absolute ceiling passed) and we still
  // can't show the finished preview. Rather than spin forever, show whatever
  // composed so far with a gentle note + a way out. (The backend reaper will
  // finalize the job server-side shortly; a refresh then shows the real state.)
  if (gaveUp && !previewReady) {
    const composed = sorted.filter((m) => m.image_url).length;
    return (
      <section className="max-w-lg mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl">This is taking longer than usual</h1>
        <p className="mt-3 text-stone-600">
          {composed > 0
            ? `We composed ${composed} of your scenes, but the rest are still catching up. Try again in a moment, or head back to refine your day.`
            : "Your wedding is still composing and hasn't finished yet. Please try again in a moment."}
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <button onClick={() => window.location.reload()}
            className="px-6 py-3 rounded-full bg-[var(--bp-accent)] text-white text-sm font-medium hover:opacity-90">Check again</button>
          <Link to="/plan" className="px-5 py-3 rounded-full border border-stone-300 text-sm hover:bg-stone-50">Back to the builder</Link>
        </div>
      </section>
    );
  }
  if (!previewReady) {
    return <GeneratingScreen moments={sorted} planned={data.job.planned_labels ?? []} terminal={Boolean(terminal)} costing={Boolean(terminal) && !costResolved} />;
  }

  const moments = sorted;
  const hero = moments[0];
  const rest = moments.slice(1);

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--bp-accent)] font-medium">Your dream wedding</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 leading-tight">The day, brought to life</h1>
          <p className="mt-2 text-stone-600 max-w-xl">Every scene below is composed to your taste. Scroll down for your vendors, costs, and a menu — then refine anything you like.</p>
          <div className="mt-5 h-px w-28 bg-gradient-to-r from-[#c9a96a] to-transparent" />
        </div>
        {breakdown && (
          <Link to={`/plan?wedding_id=${breakdown.wedding_id}`}
            className="shrink-0 px-5 py-2.5 rounded-full bg-[var(--bp-accent)] text-white text-sm font-medium hover:opacity-90">
            Edit your dream wedding ✦
          </Link>
        )}
      </header>

      {/* ── Cinematic moments grid ───────────────────────────────────────── */}
      {moments.length === 0 ? (
        <div className="rounded-2xl border border-stone-200 p-10 text-center text-stone-500">No scenes available.</div>
      ) : (
        <div className="space-y-6">
          {hero && <MomentTile moment={hero} hero getToken={stableGetToken} />}
          {rest.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {rest.map((m) => <MomentTile key={m.id} moment={m} getToken={stableGetToken} />)}
            </div>
          )}
        </div>
      )}
      <p className="mt-4 text-xs text-stone-400">AI-composed preview · generated images, not photographs.</p>

      {/* ── Breakdown: vendors · costs · menu · budget ───────────────────── */}
      <div className="mt-16">
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--bp-accent)] font-medium">Your vendors &amp; budget</div>
        <h2 className="font-display text-3xl mt-2">Your wedding, costed out</h2>
        <div className="mt-4 h-px w-28 bg-gradient-to-r from-[#c9a96a] to-transparent" />

        {!breakdown && breakdownErr && (
          <div className="mt-6 rounded-xl bg-amber-50 text-amber-800 text-sm px-4 py-3 flex items-center justify-between gap-3">
            <span>{breakdownErr}</span>
            <button onClick={() => void loadBreakdown()} className="px-3 py-1.5 rounded-full border border-amber-300 hover:bg-amber-100 text-xs">Retry</button>
          </div>
        )}

        {breakdown && (
          <>
            <BudgetBar budget={breakdown.budget} weddingId={breakdown.wedding_id} />

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {breakdown.vendors.map((v) => (
                <VendorCard key={v.vendor_id + v.category} v={v} guests={breakdown.guests} getToken={stableGetToken} />
              ))}
            </div>

            {breakdown.menu && <MenuCard menu={breakdown.menu} />}

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to={`/plan?wedding_id=${breakdown.wedding_id}`}
                className="px-6 py-3 rounded-full bg-[var(--bp-accent)] text-white text-sm font-medium hover:opacity-90">
                Edit your dream wedding ✦
              </Link>
              <Link to="/plan" className="px-5 py-3 rounded-full border border-stone-300 text-sm hover:bg-stone-50">
                Start fresh
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ─── Moment tile (the generated dream image) ─────────────────────────────────
function MomentTile({ moment, hero, getToken }: { moment: MomentOut; hero?: boolean; getToken: () => Promise<string | null> }) {
  const ar = hero ? "aspect-[16/9]" : "aspect-[4/3]";
  return (
    <div className="relative rounded-2xl overflow-hidden bg-stone-100" style={{ boxShadow: "var(--bp-shadow-card)" }}>
      {moment.image_url ? (
        <AuthedImage src={`${API_BASE()}${moment.image_url}`} alt={moment.label} getToken={getToken}
          className={`w-full ${ar} object-cover`} placeholderClassName={ar} />
      ) : (
        <div className={`w-full ${ar} flex items-center justify-center text-stone-400 text-sm`}>
          {moment.status === "ready" ? "loading…" : "composing…"}
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 to-transparent px-4 py-3">
        <h3 className={`font-display text-white ${hero ? "text-2xl" : "text-lg"}`}>{moment.label}</h3>
      </div>
    </div>
  );
}

// ─── Budget summary bar ──────────────────────────────────────────────────────
function BudgetBar({ budget, weddingId }: { budget: BreakdownResponse["budget"]; weddingId: string }) {
  const pct = budget.pct_of_target != null ? Math.max(0, Math.min(1.25, budget.pct_of_target)) : null;
  const totalStr = range(budget.estimated_low_cents, budget.estimated_high_cents);
  const targetStr = budget.target_cents != null ? dollars(budget.target_cents) : null;
  const deltaStr = budget.delta_cents != null ? dollars(Math.abs(budget.delta_cents)) : null;

  let message: React.ReactNode;
  let barColor = "var(--bp-accent)";
  if (budget.over_or_under === "no_target") {
    message = <>Estimated total <strong>{totalStr}</strong>. <Link to={`/plan?wedding_id=${weddingId}`} className="underline underline-offset-2">Add a budget</Link> to see how you're tracking.</>;
  } else if (budget.over_or_under === "under") {
    barColor = "#6b7d6a";
    message = <span className="text-[#52634f]">You're about <strong>{deltaStr}</strong> under your {targetStr} target — room to splurge on the details. 🌿</span>;
  } else if (budget.over_or_under === "on_target") {
    message = <>Right on budget — <strong>{totalStr}</strong> of your {targetStr} target.</>;
  } else {
    barColor = "#b4541f";
    message = <span className="text-[#9a3f12]">About <strong>{deltaStr}</strong> over your {targetStr} target — the estimates below flag where to trim.</span>;
  }

  return (
    <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-5" style={{ boxShadow: "var(--bp-shadow-card)" }}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="font-display text-2xl">{totalStr}<span className="text-stone-400 text-base font-sans"> estimated total</span></div>
        {targetStr && <div className="text-sm text-stone-500">Target {targetStr}</div>}
      </div>
      {pct != null && (
        <div className="mt-3 h-2 w-full rounded-full bg-stone-100 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct * 100)}%`, background: barColor }} />
        </div>
      )}
      <p className="mt-3 text-sm text-stone-600">{message}</p>
    </div>
  );
}

// ─── Vendor cost card ────────────────────────────────────────────────────────
function VendorCard({ v, guests, getToken }: { v: VendorCost; guests: number | null; getToken: () => Promise<string | null> }) {
  const cost = v.cost;
  const badge = costBadge(cost);
  const perGuest = (cost.kind === "published" || cost.kind === "estimated") && cost.basis === "per_head"
    && cost.per_guest_low_cents != null && cost.per_guest_high_cents != null
    ? `${range(cost.per_guest_low_cents, cost.per_guest_high_cents)}/guest × ${guests ?? 120}`
    : null;
  const caveat = cost.kind === "estimated" || cost.kind === "band" ? cost.caveat : null;

  return (
    <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden flex" style={{ boxShadow: "var(--bp-shadow-card)" }}>
      <div className="w-28 shrink-0 bg-stone-100">
        {v.primary_image_id ? (
          <AuthedImage src={`${API_BASE()}/admin/image/${v.primary_image_id}`} alt={v.vendor_name} getToken={getToken}
            className="w-28 h-full object-cover" placeholderClassName="w-28 h-full" />
        ) : (
          <div className="w-28 h-full flex items-center justify-center text-stone-300 text-2xl font-display">{v.label[0]}</div>
        )}
      </div>
      <div className="flex-1 min-w-0 p-4">
        <div className="text-xs uppercase tracking-wide text-stone-400">{v.label}</div>
        <Link to={`/vendor/${v.vendor_id}`} className="font-display text-lg leading-tight hover:underline block truncate">{v.vendor_name}</Link>
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          <span className="font-medium text-stone-900">{range(cost.low_cents, cost.high_cents)}</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.text}</span>
        </div>
        {perGuest && <div className="text-xs text-stone-500 mt-0.5">{perGuest}</div>}
        {caveat && <div className="text-xs text-stone-400 mt-1">{caveat}</div>}
      </div>
    </div>
  );
}

function costBadge(cost: CategoryCost): { text: string; cls: string } {
  if (cost.kind === "published") return { text: "Published price", cls: "bg-emerald-100 text-emerald-800" };
  if (cost.kind === "estimated") return { text: "Estimated", cls: "bg-[var(--bp-accent)]/15 text-[var(--bp-accent)]" };
  return { text: "Rough estimate", cls: "bg-stone-100 text-stone-500" };
}

// ─── Recommended menu card ───────────────────────────────────────────────────
function MenuCard({ menu }: { menu: NonNullable<BreakdownResponse["menu"]> }) {
  return (
    <div className="mt-8 rounded-2xl border border-stone-200 bg-[var(--bp-accent)]/[0.04] p-6" style={{ boxShadow: "var(--bp-shadow-card)" }}>
      <div className="h-px w-20 bg-gradient-to-r from-[#c9a96a] to-transparent mb-4" />
      <div className="text-xs uppercase tracking-wide text-stone-400">A menu for your day</div>
      <h3 className="font-display text-2xl mt-1">{menu.headline}</h3>
      <dl className="mt-4 space-y-3">
        {menu.courses.map((c, i) => (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-1 sm:gap-3">
            <dt className="text-sm font-medium text-[var(--bp-accent)]">{c.course}</dt>
            <dd className="text-sm text-stone-700">{c.dishes.join(" · ")}</dd>
          </div>
        ))}
      </dl>
      {menu.note && <p className="mt-4 text-sm text-stone-600 italic">{menu.note}</p>}
      <p className="mt-3 text-xs text-stone-400">Based on {menu.vendor_name}'s menu · {menu.caveat}</p>
    </div>
  );
}

// ─── Generating screen (shown until EVERYTHING is ready) ─────────────────────
// Covers both phases: composing the scenes, then costing out the wedding. The
// finished preview replaces this only once all images AND the breakdown are done.
function GeneratingScreen({ moments, planned, terminal, costing }: { moments: MomentOut[]; planned: PlannedLabel[]; terminal: boolean; costing: boolean }) {
  // Checklist over the FULL planned set in PROCESSING ORDER (builder plan §E):
  // the server (0032) returns the ordered scene plan from the first poll, so the
  // list is complete + stable immediately (no 6→10 growth, no "4 of 4"). Each row
  // flips ✓ when its moment has an image. Legacy jobs fall back to PROVISIONAL.
  const doneByLabel = new Map(moments.map((m) => [m.label, Boolean(m.image_url)]));
  const orderedLabels = planned.length > 0
    ? planned.map((p) => p.label)
    : [...PROVISIONAL, ...new Set(moments.map((m) => m.label).filter((l) => !PROVISIONAL.includes(l)))];
  const checklist = orderedLabels.map((label) => ({ label, done: doneByLabel.get(label) ?? false }));
  const imgDone = checklist.filter((c) => c.done).length;
  const imgTotal = checklist.length;
  // +1 step for the cost-out; once terminal all scenes count as done.
  const doneSteps = terminal ? imgTotal : imgDone;
  const pct = Math.round(Math.min(0.95, Math.pow(doneSteps / (imgTotal + 1), 0.85)) * 100);
  const headline = terminal ? "Costing out your wedding…" : "Bringing your day to life…";

  return (
    <div className="fixed inset-0 z-40 overflow-y-auto bp-aura">
      <div className="min-h-full flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md rounded-2xl bg-white/70 backdrop-blur-sm border border-white/60 p-6" style={{ boxShadow: "var(--bp-shadow-card)" }}>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--bp-accent)] font-medium">Composing your dream wedding</div>
          <h2 className="font-display text-2xl mt-1" aria-live="polite">{headline}</h2>
          <p className="text-sm text-stone-600 mt-1" aria-live="polite">
            {terminal ? "Every scene is ready — pricing your vendors and writing your menu." : `${imgDone} of ${imgTotal} scenes composed`}
          </p>

          <div className="mt-3 h-1.5 w-full rounded-full bg-stone-200 overflow-hidden">
            <div className="h-full bg-[var(--bp-accent)] transition-all duration-700" style={{ width: `${pct}%` }} />
          </div>

          <ul className="mt-4 space-y-1.5 text-left">
            {checklist.map((c) => (
              <li key={c.label} className="flex items-center gap-2 text-sm">
                <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${c.done ? "bg-emerald-600 text-white" : "bg-stone-200 text-stone-400"}`}>{c.done ? "✓" : "•"}</span>
                <span className={c.done ? "text-stone-800" : "text-stone-500"}>{c.label}</span>
              </li>
            ))}
            <li className="flex items-center gap-2 text-sm pt-1.5 mt-1 border-t border-stone-100">
              <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${costing ? "bg-amber-500 text-white" : "bg-stone-200 text-stone-400"}`}>{costing ? "…" : "•"}</span>
              <span className={costing ? "text-stone-800" : "text-stone-500"}>Wedding costs &amp; menu</span>
            </li>
          </ul>

          <p className="mt-4 text-xs text-stone-500">We'll reveal your whole wedding at once, the moment it's ready.</p>
        </div>
      </div>
    </div>
  );
}
